import { AbstractConnector } from '@web3-react/abstract-connector'
import { AbstractConnectorArguments, ConnectorUpdate } from '@web3-react/types'
import { requestConfiguredChain } from './switchChain'

function parseChainId(chainId: string | number | undefined): string | number | undefined {
  return typeof chainId === 'string' && chainId.startsWith('0x') ? parseInt(chainId, 16) : chainId
}

function getEthereum(): any {
  return typeof window !== 'undefined' ? (window as any).ethereum : undefined
}

export class NoEthereumProviderError extends Error {
  public constructor() {
    super()
    this.name = this.constructor.name
    this.message = 'No Ethereum provider was found on window.ethereum.'
  }
}

export class UserRejectedRequestError extends Error {
  public constructor() {
    super()
    this.name = this.constructor.name
    this.message = 'The user rejected the request.'
  }
}

export class InjectedConnector extends AbstractConnector {
  private listenersRegistered = false

  constructor(kwargs: AbstractConnectorArguments) {
    super(kwargs)

    this.handleChainChanged = this.handleChainChanged.bind(this)
    this.handleAccountsChanged = this.handleAccountsChanged.bind(this)
    this.handleDisconnect = this.handleDisconnect.bind(this)
  }

  private handleChainChanged(chainId: string | number): void {
    this.emitUpdate({ chainId: parseChainId(chainId), provider: getEthereum() })
  }

  private handleAccountsChanged(accounts: string[]): void {
    if (accounts.length === 0) {
      this.emitDeactivate()
    } else {
      this.emitUpdate({ account: accounts[0] })
    }
  }

  private handleDisconnect(): void {
    this.emitDeactivate()
  }

  private registerListeners(provider: any): void {
    if (!provider?.on || this.listenersRegistered) return
    provider.on('chainChanged', this.handleChainChanged)
    provider.on('accountsChanged', this.handleAccountsChanged)
    provider.on('disconnect', this.handleDisconnect)
    this.listenersRegistered = true
  }

  public async activate(): Promise<ConnectorUpdate> {
    const provider = getEthereum()
    if (!provider) {
      throw new NoEthereumProviderError()
    }

    if (provider.isMetaMask) {
      provider.autoRefreshOnNetworkChange = false
    }

    this.registerListeners(provider)

    try {
      await requestConfiguredChain(provider)
      const accounts: string[] = await provider.request({ method: 'eth_requestAccounts' })
      const chainId = await this.getChainId()
      return { provider, chainId, account: accounts?.[0] }
    } catch (error) {
      if ((error as any)?.code === 4001) {
        throw new UserRejectedRequestError()
      }
      throw error
    }
  }

  public async getProvider(): Promise<any> {
    return getEthereum()
  }

  public async getChainId(): Promise<number | string> {
    const provider = getEthereum()
    if (!provider) {
      throw new NoEthereumProviderError()
    }

    return parseChainId(await provider.request({ method: 'eth_chainId' })) ?? ''
  }

  public async getAccount(): Promise<null | string> {
    const provider = getEthereum()
    if (!provider) {
      throw new NoEthereumProviderError()
    }

    const accounts: string[] = await provider.request({ method: 'eth_accounts' })
    return accounts?.[0] ?? null
  }

  public deactivate(): void {
    const provider = getEthereum()
    if (provider?.removeListener && this.listenersRegistered) {
      provider.removeListener('chainChanged', this.handleChainChanged)
      provider.removeListener('accountsChanged', this.handleAccountsChanged)
      provider.removeListener('disconnect', this.handleDisconnect)
      this.listenersRegistered = false
    }
  }

  public async isAuthorized(): Promise<boolean> {
    try {
      return Boolean(await this.getAccount())
    } catch {
      return false
    }
  }
}
